/**
 * Shopify + Cloudlift Bundle Handler
 * -------------------------------------
 * This script detects when Cloudlift’s options have rendered,
 * hijacks the add-to-cart form submission for bundle products,
 * dynamically builds the line item properties, and sends a single
 * POST request to /cart/add.js with multiple variants.
 * 
 * Follow this reference for the Shopify cart API:
 * https://shopify.dev/docs/api/ajax/reference/cart
 *
 * Disables the button during request
 * Prevents Shopify default handler from running
 * Compatible with dynamic DOM injection via MutationObserver
 *
 * Author: MH Media
 * Date: 2025/05/13
 */

document.addEventListener('DOMContentLoaded', function () {
  window.addEventListener('options:rendered', function () {
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    const addToCartBtn = productForm?.querySelector('[type="submit"]');
    const optionsWrapper = document.querySelector('.cl-po--options');

    if (!productForm || !addToCartBtn || !optionsWrapper) return;

    productForm.addEventListener(
      'submit',
      function (e) {
        const isBundle = optionsWrapper.querySelector(
          '[name="properties[_option_bundle_product_id_1]"]'
        );
        if (!isBundle) return;

        e.preventDefault();
        e.stopImmediatePropagation(); // Prevent Shopify’s native form submission

        const variantIds = getBundleVariantIds(optionsWrapper);
        if (!variantIds.length) {
          console.warn('No variant IDs found.');
          return;
        }

        const properties = getLineProperties(optionsWrapper);
        const formData = buildCartFormData(variantIds, properties, optionsWrapper);

        disableAddToCartBtn(addToCartBtn, 'Adding...');

        sendToCart(formData)
          .then((result) => {
            if (window.cartDrawer) {
              window.cartDrawer.renderContents(result);
            } else if (window.cartNotification) {
              window.cartNotification.renderContents(result);
            }
          })
          .catch((error) => {
            console.error('Error adding bundle:', error);
            alert('There was an error adding your bundle. Please try again.');
          })
          .finally(() => {
            enableAddToCartBtn(addToCartBtn, 'Add to Cart');
          });
      },
      { capture: true }
    );
  });
});

/**
 * Dynamically extracts all variant IDs from Cloudlift hidden inputs.
 * @param {Element} wrapper - The DOM element containing the options.
 * @returns {string[]} Array of variant ID strings.
 */
function getBundleVariantIds(wrapper) {
  const inputs = wrapper.querySelectorAll('[name^="properties[_option_bundle_product_id_"]');
  return Array.from(inputs)
    .map((input) => input.value)
    .filter(Boolean);
}

/**
 * Extracts line item properties from the Cloudlift options UI.
 * 
 * - Collects global properties from all `option_line_*` inputs (applies to all bundle items).
 * - Detects if an `_assorted` item is present.
 *   - If so, extracts design and color selections specifically for that item.
 * 
 * @param {Element} wrapper - The DOM element containing the Cloudlift options UI.
 * @returns {{ globalProps: Object, assortedProps: Object }} An object with separate sets of properties:
 *   - `globalProps`: shared by all bundle line items
 *   - `assortedProps`: applied only to the `_assorted` bundle item
 */
function getLineProperties(wrapper) {
  const lineInputs = wrapper.querySelectorAll('[name^="properties[option_line_"]');
  const globalProps = {};
  const assortedProps = {};

  // 1. Line inputs (go to all items)
  lineInputs.forEach((input) => {
    const nameAttr = input.getAttribute('name');
    const keyMatch = nameAttr.match(/\[([^\]]+)\]/);
    if (keyMatch && input.value) {
      const label = keyMatch[1].replace('option_line_', 'Line ').replace(/_/g, ' ').trim();
      globalProps[label] = input.value;
    }
  });

  // 2. Extra: only for assorted item
  const assortedMarker = wrapper.querySelector('[data-option$="_assorted"]');
  const isAssorted = !!assortedMarker;

  if (isAssorted) {
    const selectedDesign = wrapper.querySelector('input[name="properties[design]"]:checked');
    const selectedColorSolid = wrapper.querySelector('input[name="properties[color_solid]"]:checked');
    const selectedColorBorder = wrapper.querySelector('input[name="properties[color_border]"]:checked');
    const selectedColor = selectedColorSolid || selectedColorBorder;

    if (selectedDesign?.value) {
      assortedProps['Design'] = capitalize(selectedDesign.value);
    }
    if (selectedColor?.value) {
      assortedProps['Color'] = capitalize(selectedColor.value);
    }
  }

  if (Object.keys(globalProps).length || Object.keys(assortedProps).length) {
    console.log('%c🧾 Global Line Item Properties (All Items)', 'font-size:16px;font-weight:bold;color:#2c3e50;background:#ecf0f1;padding:4px 8px;border-radius:4px;');
    Object.entries(globalProps).forEach(([key, value]) => {
      console.log(`%c${key}: %c${value}`, 'font-size:14px;color:#2980b9;', 'font-size:14px;color:#27ae60;font-weight:bold;');
    });
  
    if (Object.keys(assortedProps).length) {
      console.log('%c🎨 Assorted-Only Properties', 'font-size:16px;font-weight:bold;color:#2c3e50;background:#dff9fb;padding:4px 8px;border-radius:4px;');
      Object.entries(assortedProps).forEach(([key, value]) => {
        console.log(`%c${key}: %c${value}`, 'font-size:14px;color:#8e44ad;', 'font-size:14px;color:#e67e22;font-weight:bold;');
      });
    }
  }

  return {
    globalProps,
    assortedProps,
  };
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Builds Shopify-compatible formData for POST request to /cart/add.js.
 * @param {string[]} variantIds - List of variant IDs.
 * @param {Object} properties - Key/value object for line item properties.
 * @returns {Object} JSON object structured for Shopify cart API.
 */
function buildCartFormData(variantIds, propsObj, wrapper) {
  const { globalProps, assortedProps } = propsObj;

  const assortedWrapper = wrapper.querySelector('[data-option$="_assorted"]');
  const assortedInput = assortedWrapper?.querySelector('input');

  const assortedId = assortedInput?.value;

  return {
    items: variantIds.map((id) => {
      const item = {
        id: +id,
        quantity: 1,
        properties: { ...globalProps },
      };

      if (String(id) === assortedId) {
        Object.assign(item.properties, assortedProps);
      }

      return item;
    }),
  };
}

/**
 * Disables the Add to Cart button and updates its text.
 * @param {HTMLButtonElement} button - The button element to disable.
 * @param {string} loadingText - The text to show while loading.
 */
function disableAddToCartBtn(button, loadingText = 'Adding...') {
  button.disabled = true;
  const span = button.querySelector('span');
  if (span) {
    span.dataset.originalText = span.innerText;
    span.innerText = loadingText;
  }
}

/**
 * Enables the Add to Cart button and restores its original text.
 * @param {HTMLButtonElement} button - The button element to enable.
 * @param {string} defaultText - Default fallback text if no original was stored.
 */
function enableAddToCartBtn(button, defaultText = 'Add to Cart') {
  button.disabled = false;
  const span = button.querySelector('span');
  if (span) {
    span.innerText = span.dataset.originalText || defaultText;
    delete span.dataset.originalText;
  }
}

/**
 * Sends form data to Shopify's cart API.
 * @param {Object} formData - JSON payload for cart API.
 * @returns {Promise<Object>} Resolves with Shopify's cart response.
 */
function sendToCart(formData) {
  
  return fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  }).then((response) => {
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  });
}
